import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Badge, Button, Card, Skeleton } from "@wedding/ui";
import type { EntityRecord } from "@wedding/schemas";
import { Check, Plus, Search, Store, X } from "lucide-react";
import { api, apiFetch } from "../api";
import { taskModuleLabels, vendorTypes, type TaskModuleType } from "../config";

type LibraryItem = EntityRecord & { added: boolean };
type VendorMode = "create" | "existing";
type AddTaskResult = { task: EntityRecord; vendor: EntityRecord | null };

export function TaskLibraryPage({
  moduleType,
  embedded = false,
}: {
  moduleType: TaskModuleType;
  embedded?: boolean;
}) {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string;
  };
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [adding, setAdding] = useState<LibraryItem>();
  const [customOpen, setCustomOpen] = useState(false);
  const [taskDraft, setTaskDraft] = useState<Record<string, string>>({});
  const [vendorMode, setVendorMode] = useState<VendorMode>("create");
  const [vendorId, setVendorId] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorFinalOnly, setVendorFinalOnly] = useState(false);
  const [vendorConfirmedOnly, setVendorConfirmedOnly] = useState(false);
  const [vendorDraft, setVendorDraft] = useState<Record<string, string>>({});
  const [custom, setCustom] = useState({
    title: "",
    category: "",
    description: "",
    priority: "中",
    supplierRequired: false,
    suggestedVendorType: "待选择",
  });
  const addInFlight = useRef(false);

  const library = useQuery({
    queryKey: [workspaceId, "task-library", moduleType],
    queryFn: () =>
      apiFetch<LibraryItem[]>(
        `/workspaces/${workspaceId}/task-library/items/all?moduleType=${encodeURIComponent(moduleType)}`,
      ),
  });
  const vendors = useQuery({
    queryKey: [workspaceId, "vendors"],
    queryFn: () =>
      api.list(workspaceId, "vendors").then((result) => result.data),
  });

  const categories = useMemo(
    () =>
      [
        ...new Set(
          (library.data ?? []).map((item) =>
            String(item.data.category ?? "其他"),
          ),
        ),
      ].sort(),
    [library.data],
  );
  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return (library.data ?? []).filter((item) => {
      const matchesSearch =
        !keyword ||
        [item.data.title, item.data.description, item.data.category].some(
          (value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(keyword),
        );
      return matchesSearch && (!category || item.data.category === category);
    });
  }, [library.data, search, category]);
  const availableVendors = useMemo(() => {
    const keyword = vendorSearch.trim().toLowerCase();
    let recent: string[] = [];
    try {
      recent = JSON.parse(
        localStorage.getItem("wedding-recent-vendors") ?? "[]",
      ) as string[];
    } catch {
      recent = [];
    }
    return [...(vendors.data ?? [])]
      .filter(
        (vendor) =>
          (!keyword ||
            ["vendorName", "vendorType", "contact", "phone"].some((key) =>
              String(vendor.data[key] ?? "")
                .toLowerCase()
                .includes(keyword),
            )) &&
          (!vendorFinalOnly || Boolean(vendor.data.finalSelected)) &&
          (!vendorConfirmedOnly || Boolean(vendor.data.availabilityConfirmed)),
      )
      .sort((left, right) => {
        const leftRecent = recent.indexOf(left.id);
        const rightRecent = recent.indexOf(right.id);
        if (leftRecent >= 0 || rightRecent >= 0)
          return (
            (leftRecent < 0 ? 99 : leftRecent) -
            (rightRecent < 0 ? 99 : rightRecent)
          );
        return String(left.data.vendorName ?? "").localeCompare(
          String(right.data.vendorName ?? ""),
          "zh-CN",
        );
      });
  }, [vendorConfirmedOnly, vendorFinalOnly, vendorSearch, vendors.data]);

  const addTask = useMutation({
    mutationFn: () => {
      if (!adding) throw new Error("请选择待办");
      const supplierRequired = Boolean(adding.data.supplierRequired);
      const selectedVendor = vendors.data?.find(
        (vendor) => vendor.id === vendorId,
      );
      return apiFetch<AddTaskResult>(
        `/workspaces/${workspaceId}/task-library/add`,
        {
          method: "POST",
          body: JSON.stringify({
            templateId: adding.id,
            moduleType,
            task: taskDraft,
            ...(supplierRequired
              ? {
                  vendor:
                    vendorMode === "existing"
                      ? {
                          mode: "existing",
                          vendorId,
                          expectedVersion: selectedVendor?.version,
                          data: vendorDraft,
                        }
                      : { mode: "create", data: vendorDraft },
                }
              : {}),
          }),
        },
      );
    },
    onSuccess: (result) => {
      queryClient.setQueryData<EntityRecord[]>([workspaceId, "tasks"], (old) =>
        old
          ? old.some((task) => task.id === result.task.id)
            ? old.map((task) =>
                task.id === result.task.id ? result.task : task,
              )
            : [result.task, ...old]
          : old,
      );
      if (result.vendor)
        queryClient.setQueryData<EntityRecord[]>(
          [workspaceId, "vendors"],
          (old) =>
            old
              ? old.some((vendor) => vendor.id === result.vendor!.id)
                ? old.map((vendor) =>
                    vendor.id === result.vendor!.id ? result.vendor! : vendor,
                  )
                : [result.vendor!, ...old]
              : old,
        );
      queryClient.setQueryData<LibraryItem[]>(
        [workspaceId, "task-library", moduleType],
        (old) =>
          old?.map((item) =>
            item.id === result.task.data.sourceTemplateId
              ? { ...item, added: true }
              : item,
          ),
      );
      setAdding(undefined);
    },
  });

  const createCustom = useMutation({
    mutationFn: () =>
      api.create(workspaceId, "task-templates", {
        ...custom,
        moduleType,
        origin: "custom",
        status: "未开始",
      }),
    onSuccess: async () => {
      setCustomOpen(false);
      setCustom({
        title: "",
        category: "",
        description: "",
        priority: "中",
        supplierRequired: false,
        suggestedVendorType: "待选择",
      });
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "task-library", moduleType],
      });
    },
  });

  const openAdd = (item: LibraryItem) => {
    const suggestedVendorType = String(
      item.data.suggestedVendorType ?? "待选择",
    );
    setAdding(item);
    setTaskDraft({
      title: String(item.data.title ?? ""),
      owner: String(item.data.owner ?? ""),
      dueDate: String(item.data.dueDate ?? ""),
    });
    setVendorMode("create");
    setVendorId("");
    setVendorSearch("");
    setVendorFinalOnly(false);
    setVendorConfirmedOnly(false);
    setVendorDraft({
      vendorType: suggestedVendorType,
      vendorName: `${suggestedVendorType}待选择`,
      contact: "",
      phone: "",
    });
  };

  const chooseExistingVendor = (id: string) => {
    setVendorId(id);
    if (id) {
      let recent: string[] = [];
      try {
        recent = JSON.parse(
          localStorage.getItem("wedding-recent-vendors") ?? "[]",
        ) as string[];
      } catch {
        recent = [];
      }
      localStorage.setItem(
        "wedding-recent-vendors",
        JSON.stringify(
          [id, ...recent.filter((item) => item !== id)].slice(0, 8),
        ),
      );
    }
    const selected = vendors.data?.find((vendor) => vendor.id === id);
    setVendorDraft(
      selected
        ? {
            vendorType: String(selected.data.vendorType ?? "待选择"),
            vendorName: String(selected.data.vendorName ?? ""),
            contact: String(selected.data.contact ?? ""),
            phone: String(selected.data.phone ?? ""),
          }
        : {},
    );
  };

  const submitAddTask = () => {
    if (addInFlight.current) return;
    addInFlight.current = true;
    addTask.mutate(undefined, {
      onSettled: () => {
        addInFlight.current = false;
      },
    });
  };

  return (
    <div
      className={`${embedded ? "module-library" : "page"} task-library-page`}
    >
      {!embedded && (
        <header className="page-header">
          <div>
            <span className="eyebrow">模块待办</span>
            <h1>{taskModuleLabels[moduleType] ?? "模块"}待办库</h1>
            <p>
              只挑选这个模块真正需要的事项；加入后同时出现在模块待办和总任务清单。
            </p>
          </div>
          <Button onClick={() => setCustomOpen(true)}>
            <Plus size={17} /> 新建自定义待办
          </Button>
        </header>
      )}
      <section className="library-toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="搜索待办模板…"
          />
        </div>
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          <option value="">全部分类</option>
          {categories.map((item) => (
            <option key={item}>{item}</option>
          ))}
        </select>
        <span>{filtered.length} 个可选事项</span>
        {embedded && (
          <Button onClick={() => setCustomOpen(true)}>
            <Plus size={17} /> 新建自定义事项
          </Button>
        )}
      </section>
      {library.isLoading ? (
        <Card>
          <Skeleton lines={10} />
        </Card>
      ) : (
        <section className="library-grid">
          {filtered.map((item) => (
            <Card
              key={item.id}
              className={item.added ? "library-card added" : "library-card"}
            >
              <header>
                <div>
                  <Badge>
                    {String(item.data.phase ?? item.data.category ?? "待办")}
                  </Badge>
                  {item.data.origin === "custom" && <Badge>自定义</Badge>}
                </div>
                {Boolean(item.data.supplierRequired) && (
                  <span className="supplier-mark">
                    <Store size={14} />
                    {String(item.data.suggestedVendorType)}
                  </span>
                )}
              </header>
              <h2>{String(item.data.title)}</h2>
              <p>{String(item.data.description ?? "暂无说明")}</p>
              <footer>
                <span>{String(item.data.owner ?? "负责人待定")}</span>
                <Button
                  className={item.added ? "secondary" : ""}
                  disabled={item.added}
                  onClick={() => openAdd(item)}
                >
                  {item.added ? (
                    <>
                      <Check size={15} /> 已在清单
                    </>
                  ) : (
                    <>
                      <Plus size={15} /> 加入待办
                    </>
                  )}
                </Button>
              </footer>
            </Card>
          ))}
        </section>
      )}

      {adding && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="加入待办"
          >
            <header>
              <div>
                <span className="eyebrow">加入待办</span>
                <h2>加入待办清单</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setAdding(undefined)}
                aria-label="关闭"
              >
                <X />
              </button>
            </header>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                submitAddTask();
              }}
            >
              <div className="form-grid">
                <label className="span-2">
                  <span>待办名称</span>
                  <input
                    required
                    value={taskDraft.title ?? ""}
                    onChange={(event) =>
                      setTaskDraft({ ...taskDraft, title: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>负责人</span>
                  <input
                    value={taskDraft.owner ?? ""}
                    onChange={(event) =>
                      setTaskDraft({ ...taskDraft, owner: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>截止日期</span>
                  <input
                    type="date"
                    value={taskDraft.dueDate ?? ""}
                    onChange={(event) =>
                      setTaskDraft({
                        ...taskDraft,
                        dueDate: event.target.value,
                      })
                    }
                  />
                </label>
              </div>
              {Boolean(adding.data.supplierRequired) && (
                <section className="vendor-inline">
                  <header>
                    <Store />
                    <div>
                      <h3>关联供应商</h3>
                      <p>保存待办时同步写入供应商库。</p>
                    </div>
                  </header>
                  <div className="vendor-mode">
                    <label>
                      <input
                        type="radio"
                        checked={vendorMode === "create"}
                        onChange={() => setVendorMode("create")}
                      />{" "}
                      新建供应商
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={vendorMode === "existing"}
                        onChange={() => setVendorMode("existing")}
                      />{" "}
                      使用已有供应商
                    </label>
                  </div>
                  {vendorMode === "existing" && (
                    <div className="vendor-select relation-combobox">
                      <label>
                        <span>搜索供应商</span>
                        <input
                          type="search"
                          value={vendorSearch}
                          onChange={(event) =>
                            setVendorSearch(event.target.value)
                          }
                          placeholder="输入名称、类型、联系人或电话"
                        />
                      </label>
                      <div className="relation-filter-row">
                        <label>
                          <input
                            type="checkbox"
                            checked={vendorFinalOnly}
                            onChange={(event) =>
                              setVendorFinalOnly(event.target.checked)
                            }
                          />{" "}
                          仅看最终选择
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={vendorConfirmedOnly}
                            onChange={(event) =>
                              setVendorConfirmedOnly(event.target.checked)
                            }
                          />{" "}
                          仅看档期已确认
                        </label>
                        <span>{availableVendors.length} 个可选</span>
                      </div>
                      <label>
                        <span>选择供应商</span>
                        <select
                          required
                          value={vendorId}
                          onChange={(event) =>
                            chooseExistingVendor(event.target.value)
                          }
                        >
                          <option value="">请选择</option>
                          {availableVendors.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {String(vendor.data.vendorName)} ·{" "}
                              {String(vendor.data.vendorType)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  <div className="form-grid">
                    <label>
                      <span>供应商类型</span>
                      <select
                        required
                        value={vendorDraft.vendorType ?? ""}
                        onChange={(event) =>
                          setVendorDraft({
                            ...vendorDraft,
                            vendorType: event.target.value,
                          })
                        }
                      >
                        {vendorTypes.map((type) => (
                          <option key={type}>{type}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>供应商名称</span>
                      <input
                        required
                        value={vendorDraft.vendorName ?? ""}
                        onChange={(event) =>
                          setVendorDraft({
                            ...vendorDraft,
                            vendorName: event.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>联系人</span>
                      <input
                        value={vendorDraft.contact ?? ""}
                        onChange={(event) =>
                          setVendorDraft({
                            ...vendorDraft,
                            contact: event.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>电话</span>
                      <input
                        value={vendorDraft.phone ?? ""}
                        onChange={(event) =>
                          setVendorDraft({
                            ...vendorDraft,
                            phone: event.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                </section>
              )}
              {addTask.error && (
                <p className="form-error">{addTask.error.message}</p>
              )}
              <footer>
                <Button
                  type="button"
                  className="secondary"
                  onClick={() => setAdding(undefined)}
                >
                  取消
                </Button>
                <Button type="submit" disabled={addTask.isPending}>
                  {addTask.isPending ? "正在加入…" : "确认加入"}
                </Button>
              </footer>
            </form>
          </aside>
        </div>
      )}

      {customOpen && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="新建自定义待办"
          >
            <header>
              <div>
                <span className="eyebrow">自定义事项</span>
                <h2>新建自定义待办</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setCustomOpen(false)}
                aria-label="关闭"
              >
                <X />
              </button>
            </header>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                createCustom.mutate();
              }}
            >
              <div className="form-grid">
                <label className="span-2">
                  <span>待办名称</span>
                  <input
                    required
                    value={custom.title}
                    onChange={(event) =>
                      setCustom({ ...custom, title: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>分类</span>
                  <input
                    required
                    value={custom.category}
                    onChange={(event) =>
                      setCustom({ ...custom, category: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>优先级</span>
                  <select
                    value={custom.priority}
                    onChange={(event) =>
                      setCustom({ ...custom, priority: event.target.value })
                    }
                  >
                    <option>高</option>
                    <option>中</option>
                    <option>低</option>
                  </select>
                </label>
                <label className="span-2">
                  <span>说明</span>
                  <textarea
                    rows={4}
                    value={custom.description}
                    onChange={(event) =>
                      setCustom({ ...custom, description: event.target.value })
                    }
                  />
                </label>
                <label className="span-2 inline-library-check">
                  <input
                    type="checkbox"
                    checked={custom.supplierRequired}
                    onChange={(event) =>
                      setCustom({
                        ...custom,
                        supplierRequired: event.target.checked,
                      })
                    }
                  />{" "}
                  这个待办需要供应商
                </label>
                {custom.supplierRequired && (
                  <label>
                    <span>建议供应商类型</span>
                    <select
                      value={custom.suggestedVendorType}
                      onChange={(event) =>
                        setCustom({
                          ...custom,
                          suggestedVendorType: event.target.value,
                        })
                      }
                    >
                      {vendorTypes.map((type) => (
                        <option key={type}>{type}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              {createCustom.error && (
                <p className="form-error">{createCustom.error.message}</p>
              )}
              <footer>
                <Button
                  type="button"
                  className="secondary"
                  onClick={() => setCustomOpen(false)}
                >
                  取消
                </Button>
                <Button type="submit" disabled={createCustom.isPending}>
                  {createCustom.isPending ? "保存中…" : "保存到待办库"}
                </Button>
              </footer>
            </form>
          </aside>
        </div>
      )}
    </div>
  );
}
